import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { FormDialog } from '@/components/shared/form-dialog';
import { FormField } from '@/components/shared/form-field';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';

export interface GalleryCategory {
  id: string;
  name: string;
  isSystem: boolean;
  galleryCount: number;
}

interface GalleryCategoriesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const GalleryCategoriesDialog = ({ open, onOpenChange }: GalleryCategoriesDialogProps) => {
  const { t } = useTranslation('content');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<GalleryCategory | null>(null);
  const [name, setName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<GalleryCategory | null>(null);

  const categories = useQuery({
    queryKey: ['content', 'gallery-categories'],
    queryFn: () =>
      api.get<{ data: GalleryCategory[] }>('/content/gallery-categories', {
        query: { page: 1, pageSize: 100 },
      }),
    enabled: open,
  });

  const rows = categories.data?.data ?? [];

  const resetForm = () => {
    setEditing(null);
    setName('');
    setFormOpen(false);
  };

  const openCreate = () => {
    setEditing(null);
    setName('');
    setFormOpen(true);
  };

  const openEdit = (category: GalleryCategory) => {
    setEditing(category);
    setName(category.name);
    setFormOpen(true);
  };

  const save = useMutation({
    mutationFn: () => {
      const payload = { name: name.trim() };
      if (editing) {
        return api.patch<GalleryCategory>(`/content/gallery-categories/${editing.id}`, payload);
      }
      return api.post<GalleryCategory>('/content/gallery-categories', payload);
    },
    onSuccess: () => {
      toast.success(editing ? t('galleries.categories.saved') : t('galleries.categories.created'));
      resetForm();
      void queryClient.invalidateQueries({ queryKey: ['content', 'gallery-categories'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/content/gallery-categories/${id}`),
    onSuccess: () => {
      toast.success(t('galleries.categories.deleted'));
      setDeleteTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['content', 'gallery-categories'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const columns: DataTableColumn<GalleryCategory>[] = [
    {
      id: 'name',
      header: t('galleries.categories.columns.name'),
      cell: (row) => row.name,
    },
    {
      id: 'galleries',
      header: t('galleries.categories.columns.galleries'),
      cell: (row) => t('galleries.categories.count', { count: row.galleryCount }),
      hideBelowMd: true,
    },
  ];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('galleries.categories.title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button size="sm" onClick={openCreate}>
                {t('galleries.categories.add')}
              </Button>
            </div>
            <DataTable
              rows={rows}
              columns={columns}
              rowKey={(row) => row.id}
              loading={categories.isLoading}
              rowActions={(row) => (
                <div className="flex justify-end gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={t('galleries.categories.edit')}
                    onClick={(event) => {
                      event.stopPropagation();
                      openEdit(row);
                    }}
                  >
                    <Pencil className="size-4" aria-hidden />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={t('galleries.categories.delete')}
                    disabled={row.galleryCount > 0}
                    onClick={(event) => {
                      event.stopPropagation();
                      setDeleteTarget(row);
                    }}
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                </div>
              )}
              actionsHeader={tCommon('table.actions')}
            />
            <p className="text-body-sm text-fg-secondary">{t('galleries.categories.hint')}</p>
          </div>
        </DialogContent>
      </Dialog>

      <FormDialog
        open={formOpen}
        onOpenChange={(next) => {
          if (!next) resetForm();
          else setFormOpen(true);
        }}
        title={editing ? t('galleries.categories.editTitle') : t('galleries.categories.addTitle')}
        size="sm"
        submitLabel={editing ? t('galleries.categories.save') : t('galleries.categories.add')}
        cancelLabel={tCommon('action.cancel')}
        submitting={save.isPending}
        submitDisabled={name.trim().length === 0}
        onSubmit={() => save.mutate()}
      >
        <FormField label={t('galleries.categories.name')} required>
          {(field) => (
            <Input
              id={field.id}
              value={name}
              onChange={(event) => setName(event.target.value)}
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>
      </FormDialog>

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(next) => {
          if (!next) setDeleteTarget(null);
        }}
        title={t('galleries.categories.deleteConfirmNamed', { name: deleteTarget?.name ?? '' })}
        description={t('galleries.categories.deleteConfirm')}
        confirmLabel={t('galleries.categories.delete')}
        cancelLabel={tCommon('action.cancel')}
        loading={remove.isPending}
        onConfirm={() => {
          if (deleteTarget) remove.mutate(deleteTarget.id);
        }}
      />
    </>
  );
};
